import React, { useEffect, useState } from 'react';
import { Trash2, AlertTriangle, X, Clock, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useVenue } from '../contexts/VenueContext';
import { fetchSelectedVenueApplication, deleteVenue, cancelVenueDeletion } from '../utils/venueAppApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const font = "'Inria Serif', serif";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return iso;
  }
};

/**
 * Delete-VENUE section — scoped strictly to the venue currently selected in
 * the top-left switcher (`activeVenueAppId`), never the whole account or
 * any other venue the owner might also have. Mirrors `DeleteAccountSection`'s
 * 14-day soft-delete grace period pattern (transactions/invoices/reviews are
 * never touched — kept for DAC7/financial retention).
 */
export const DeleteVenueSection = ({ dataTestIdPrefix = 'venue-settings' }) => {
  const { activeVenueAppId, activeVenue, refresh } = useVenue();
  const [venueDoc, setVenueDoc] = useState(activeVenue || null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(true); // assume true until we know otherwise

  useEffect(() => {
    // Check if user has a password (Google-only users don't)
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/account/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setHasPassword(!!res.data?.security?.has_password))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeVenueAppId) return;
      try {
        const app = await fetchSelectedVenueApplication(activeVenueAppId);
        if (!cancelled && app) setVenueDoc(app);
      } catch (_) { /* fall back to context value */ }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  const isPending = !!venueDoc?.deletion_pending;
  const finalizeAt = venueDoc?.deletion_finalize_at;
  const venueName = venueDoc?.restaurant_name || activeVenue?.restaurant_name || 'this venue';

  const reset = () => {
    setPassword('');
    setConfirmation('');
    setReason('');
    setLoading(false);
  };

  const closeDelete = () => { if (!loading) { setDeleteOpen(false); reset(); } };
  const closeCancel = () => { if (!loading) { setCancelOpen(false); reset(); } };

  const handleSchedule = async () => {
    if (confirmation.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    if (hasPassword && !password) {
      toast.error('Enter your password to confirm.');
      return;
    }
    setLoading(true);
    try {
      const res = await deleteVenue(activeVenueAppId, { password, confirmation, reason });
      setVenueDoc((prev) => ({ ...(prev || {}), deletion_pending: true, deletion_finalize_at: res.deletion_finalize_at }));
      toast.success(`${venueName} is scheduled for deletion on ${formatDate(res.deletion_finalize_at)}`);
      setDeleteOpen(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not schedule deletion. Please try again.');
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (hasPassword && !password) {
      toast.error('Enter your password to confirm.');
      return;
    }
    setLoading(true);
    try {
      await cancelVenueDeletion(activeVenueAppId, { password });
      setVenueDoc((prev) => ({ ...(prev || {}), deletion_pending: false, deletion_finalize_at: null }));
      toast.success(`${venueName} is no longer scheduled for deletion.`);
      closeCancel();
      if (refresh) refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel deletion.');
      setLoading(false);
    }
  };

  if (!activeVenueAppId) return null;

  if (isPending) {
    return (
      <>
        <div
          data-testid={`${dataTestIdPrefix}-venue-deletion-pending-section`}
          style={{ marginTop: 24, padding: 16, borderRadius: 14, border: '1px solid var(--card-border)', background: 'var(--status-warning-surface)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Clock size={20} color="var(--status-warning-text)" />
            <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-warning-text)', margin: 0 }}>
              {venueName} — deletion scheduled
            </p>
          </div>
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
            This venue will come off the guest map on{' '}
            <strong>{finalizeAt ? formatDate(finalizeAt) : 'the scheduled date'}</strong>. You can change
            your mind any time before that date. Your other venues (if any) and your account are not affected.
          </p>
          <button
            type="button"
            onClick={() => setCancelOpen(true)}
            data-testid={`${dataTestIdPrefix}-cancel-venue-deletion-button`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: 'none', background: 'var(--status-warning-text)', color: '#fff', fontFamily: font, fontSize: 14, fontWeight: 700, borderRadius: 10, cursor: 'pointer' }}
          >
            <Undo2 size={16} /> Cancel deletion
          </button>
        </div>

        {cancelOpen && (
          <div
            data-testid={`${dataTestIdPrefix}-cancel-venue-deletion-modal`}
            onClick={closeCancel}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: 'var(--surface)', borderRadius: 16, padding: 22, boxShadow: 'var(--premium-shadow-main)', fontFamily: font }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Cancel deletion?</h3>
                <button type="button" onClick={closeCancel} disabled={loading} data-testid={`${dataTestIdPrefix}-cancel-venue-modal-close`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text)' }} aria-label="Close">
                  <X size={20} />
                </button>
              </div>
              <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55, margin: '0 0 16px' }}>
                This will keep {venueName} live and remove the scheduled deletion.
              </p>
              {hasPassword ? (
                <>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>Confirm with your password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={`${dataTestIdPrefix}-cancel-venue-modal-password`}
                    placeholder="Enter your password"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 14, fontFamily: font, marginBottom: 18, boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--text)' }}
                  />
                </>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--text-3)', background: 'var(--accent-surface-soft)', padding: '8px 12px', borderRadius: 10, marginBottom: 18, lineHeight: 1.4 }}>
                  Your identity is verified via Google — no password required.
                </p>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={closeCancel} disabled={loading} data-testid={`${dataTestIdPrefix}-cancel-venue-modal-dismiss`} style={{ flex: 1, padding: '11px 0', border: '1px solid var(--hairline)', background: 'var(--surface)', color: 'var(--text)', fontFamily: font, fontSize: 14, fontWeight: 600, borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
                  Keep deletion
                </button>
                <button type="button" onClick={handleCancel} disabled={loading} data-testid={`${dataTestIdPrefix}-cancel-venue-modal-submit`} style={{ flex: 1, padding: '11px 0', border: 'none', background: 'var(--status-success-text)', color: '#fff', fontFamily: font, fontSize: 14, fontWeight: 700, borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Cancelling…' : 'Cancel deletion'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div
        data-testid={`${dataTestIdPrefix}-delete-venue-section`}
        style={{ marginTop: 24, padding: 16, borderRadius: 14, border: '1px solid var(--card-border)', background: 'var(--status-danger-surface)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Trash2 size={20} color="var(--status-danger-text)" />
          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-danger-text)', margin: 0 }}>
            Delete this venue
          </p>
        </div>
        <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Removes <strong>{venueName}</strong> only — your account and any other venues you own are not
          affected. You&apos;ll have <strong>14 days</strong> to cancel before it comes off the guest map.
          Past transactions, invoices and reviews are kept for legal/tax retention.
        </p>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          data-testid={`${dataTestIdPrefix}-delete-venue-button`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: '1px solid var(--status-danger-text)', background: 'var(--surface)', color: 'var(--status-danger-text)', fontFamily: font, fontSize: 14, fontWeight: 700, borderRadius: 10, cursor: 'pointer' }}
        >
          <Trash2 size={16} /> Delete this venue
        </button>
      </div>

      {deleteOpen && (
        <div
          data-testid={`${dataTestIdPrefix}-delete-venue-modal`}
          onClick={closeDelete}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: 'var(--surface)', borderRadius: 16, padding: 22, boxShadow: 'var(--premium-shadow-main)', fontFamily: font }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={22} color="var(--status-danger-text)" />
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Delete {venueName}?</h3>
              </div>
              <button type="button" onClick={closeDelete} disabled={loading} data-testid={`${dataTestIdPrefix}-delete-venue-modal-close`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text)' }} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55, margin: '0 0 16px' }}>
              {venueName} enters a <strong>14-day grace period</strong>. Sign in any time during that
              window and tap <em>Cancel deletion</em> to restore it. After 14 days it comes off the guest
              map and staff access is revoked. Any other venue you own is untouched.
            </p>

            {hasPassword ? (
              <>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>Current password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid={`${dataTestIdPrefix}-delete-venue-modal-password`}
                  placeholder="Enter your password"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 14, fontFamily: font, marginBottom: 14, boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--text)' }}
                />
              </>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-3)', background: 'var(--accent-surface-soft)', padding: '8px 12px', borderRadius: 10, marginBottom: 14, lineHeight: 1.4 }}>
                Your identity is verified via Google — no password required.
              </p>
            )}

            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
              Type <span style={{ color: 'var(--status-danger-text)' }}>DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              data-testid={`${dataTestIdPrefix}-delete-venue-modal-confirm`}
              placeholder="DELETE"
              autoCapitalize="characters"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 14, fontFamily: font, marginBottom: 14, boxSizing: 'border-box', letterSpacing: 1, background: 'var(--surface)', color: 'var(--text)' }}
            />

            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`${dataTestIdPrefix}-delete-venue-modal-reason`}
              placeholder="Help us improve Allure..."
              rows={3}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 14, fontFamily: font, marginBottom: 18, boxSizing: 'border-box', resize: 'vertical', background: 'var(--surface)', color: 'var(--text)' }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={closeDelete} disabled={loading} data-testid={`${dataTestIdPrefix}-delete-venue-modal-cancel`} style={{ flex: 1, padding: '11px 0', border: '1px solid var(--hairline)', background: 'var(--surface)', color: 'var(--text)', fontFamily: font, fontSize: 14, fontWeight: 600, borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
                Cancel
              </button>
              <button type="button" onClick={handleSchedule} disabled={loading} data-testid={`${dataTestIdPrefix}-delete-venue-modal-submit`} style={{ flex: 1, padding: '11px 0', border: 'none', background: 'var(--status-danger-text)', color: '#fff', fontFamily: font, fontSize: 14, fontWeight: 700, borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Scheduling…' : 'Schedule deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteVenueSection;
